"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Zap, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  id: 1,
  title: "1064nm Laser",
  description: "Eliminates fluorescence interference from dark or cutting agents"
}, {
  id: 2,
  title: "Rapid Screening",
  description: "Accurate identification of pure substances and complex mixtures in seconds"
}, {
  id: 3,
  title: "Comprehensive Library",
  description: "Expansive narcotics, fentanyl analogs, and precursor database"
}];
export default function HeroNarcotics() {
  return <section id="heronarcoticsidentification" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 lg:py-28 overflow-hidden border-b border-border">
      {/* Decorative background grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column - 8 Cols */}
          <div className="lg:col-span-8 flex flex-col items-start text-left">
            {/* Status Badge */}
            <div className="mb-6">
              <Badge variant="outline" className="px-3.5 py-1.5 text-xs md:text-sm font-medium border-border bg-secondary text-secondary-foreground rounded-full flex items-center gap-2 shadow-xs">
                <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
                <span>1064nm Raman Narcotics Identification</span>
              </Badge>
            </div>

            {/* Main H1 Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-foreground max-w-3xl leading-[1.12]">
              CQL Narc-ID Handheld Narcotics Identification Platform
            </h1>

            {/* Factual Lead Paragraph */}
            <p className="mt-6 text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              Engineered for rapid field narcotics screening, the CQL Narc-ID leverages 1064nm Raman spectroscopy to penetrate colored packaging, suppress fluorescence, and deliver decisive identification of illicit drugs, fentanyl analogs, and cutting agents within seconds.
            </p>

            {/* Dual CTA Buttons */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-md font-medium text-base shadow-sm">
                <Link to="/BookADemoPage">
                  Book a Demo
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md font-medium text-base border-border bg-background hover:bg-secondary text-foreground">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Your Application
                </Link>
              </Button>
            </div>

            {/* Key Capability Highlights */}
            <div className="mt-12 pt-8 border-t border-border w-full grid grid-cols-1 sm:grid-cols-3 gap-6">
              {features.map((feature, index) => <div key={feature.id} data-index={index} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="font-semibold text-sm text-foreground">
                      {feature.title}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal pl-6">
                    {feature.description}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Right Column - 4 Cols - Device Render */}
          <div className="lg:col-span-4 w-full flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md lg:max-w-none">
              {/* Subtle ambient glow behind device */}
              <div className="absolute -inset-4 bg-primary/10 rounded-2xl blur-2xl -z-10" />

              {/* Device Card Container */}
              <div className="relative rounded-xl border border-border bg-card p-6 shadow-md overflow-hidden">
                {/* Visual Header Tag */}
                <div className="flex items-center justify-between pb-4 mb-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-5 text-primary" />
                    <span className="text-xs font-semibold tracking-wide uppercase text-foreground">
                      Field Ready Spec
                    </span>
                  </div>
                  <Badge variant="secondary" className="text-2xs uppercase tracking-wider font-mono">
                    MIL-STD-810G
                  </Badge>
                </div>

                {/* Device Image Render */}
                <div className="relative aspect-4/5 rounded-lg overflow-hidden bg-muted/50 flex items-center justify-center p-4">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_narc_id_handheld_device.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_narc_id_handheld_device.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_narc_id_handheld_device.png-wvc-sizes" alt="CQL Narc-ID handheld Raman spectrometer for narcotics screening" loading="lazy" decoding="async" className="w-full h-full object-contain object-center transition-transform duration-300 hover:scale-105" />
                </div>

                {/* Technical Specs Footer */}
                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    <Zap className="size-3.5 text-primary" /> 1064nm Excitation
                  </span>
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    <Sparkles className="size-3.5 text-primary" /> Non-destructive
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}